(() => {
  type LauncherMode = 'text' | 'voice' | 'both'

  const launcherIcon = (mode: LauncherMode) => {
    if (mode === 'text') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"/></svg>'
    }
    if (mode === 'voice') {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8"/></svg>'
    }
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 11.5a7.5 7.5 0 0 1-8 7.47 8.7 8.7 0 0 1-3.7-.83L4 20l1.4-3.75A7.5 7.5 0 1 1 20 11.5Z"/><path d="M8.5 12v-1M11 13.5v-4M13.5 12.5v-2M16 12v-1"/></svg>'
  }

  const script = document.currentScript as HTMLScriptElement | null
  const publicKey =
    script?.dataset.widgetId?.trim() || script?.dataset.key?.trim()
  if (!script || !publicKey || script.dataset.rynvioMounted === 'true') return
  script.dataset.rynvioMounted = 'true'

  const scriptUrl = new URL(script.src, window.location.href)
  const widgetOrigin = (script.dataset.widgetOrigin || scriptUrl.origin).replace(/\/$/, '')
  const apiBase = (script.dataset.api || 'https://api.rynvio.app').replace(/\/$/, '')
  const hostOrigin = window.location.origin
  const position = script.dataset.position === 'left' ? 'left' : 'right'
  const mount = document.createElement('div')
  mount.id = `rynvio-widget-${publicKey.slice(-8)}`
  document.body.append(mount)
  const applyPosition = (value: unknown) => {
    mount.dataset.position = value === 'left' ? 'left' : 'right'
  }
  const applyPrimaryColor = (value: unknown) => {
    if (typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)) {
      mount.style.setProperty('--rynvio-primary', value)
    }
  }
  applyPosition(position)
  const root = mount.attachShadow({ mode: 'open' })
  const stylesheet = document.createElement('link')
  stylesheet.rel = 'stylesheet'
  stylesheet.href = `${widgetOrigin}/loader.css`
  const launcher = document.createElement('button')
  launcher.className = 'launcher'
  launcher.type = 'button'
  launcher.setAttribute('aria-label', 'AI受付を開く')
  const mark = document.createElement('span')
  mark.className = 'mark'
  mark.setAttribute('aria-hidden', 'true')
  mark.innerHTML = launcherIcon('both')
  const label = document.createElement('span')
  label.className = 'label'
  label.textContent = 'AIに相談'
  launcher.append(mark, label)
  root.append(stylesheet, launcher)

  launcher.hidden = true
  let frame: HTMLIFrameElement | null = null
  let bootstrapMessage: Record<string, unknown> | null = null
  let opening = false
  let refreshing = false
  let launcherLabel = 'AIに相談'
  let launcherMode: LauncherMode = 'both'
  let lastPageUrl = window.location.href
  let lastPageTitle = document.title
  let contextReferrer = document.referrer

  const applyLauncherMode = (value: unknown) => {
    launcherMode = value === 'text' || value === 'voice' ? value : 'both'
    mark.innerHTML = launcherIcon(launcherMode)
    launcher.dataset.mode = launcherMode
    launcher.setAttribute(
      'aria-label',
      launcherMode === 'voice' ? '音声受付を開く' : launcherMode === 'text' ? 'テキスト受付を開く' : 'テキスト・音声受付を開く',
    )
  }

  const modeFromConfig = (config: { text_enabled?: boolean; voice_enabled?: boolean } | undefined) => {
    if (!config) return launcherMode
    if (config.text_enabled === false && config.voice_enabled !== false) return 'voice'
    if (config.voice_enabled === false && config.text_enabled !== false) return 'text'
    return 'both'
  }

  applyLauncherMode('both')

  const syncPageContext = () => {
    const nextUrl = window.location.href
    const nextTitle = document.title.slice(0, 512)
    if (nextUrl === lastPageUrl && nextTitle === lastPageTitle) return
    if (nextUrl !== lastPageUrl) contextReferrer = lastPageUrl
    lastPageUrl = nextUrl
    lastPageTitle = nextTitle
    frame?.contentWindow?.postMessage(
      {
        type: 'rynvio:context',
        pageUrl: lastPageUrl,
        pageTitle: lastPageTitle,
        referrerUrl: contextReferrer,
      },
      widgetOrigin,
    )
  }

  window.addEventListener('popstate', syncPageContext)
  window.addEventListener('hashchange', syncPageContext)
  window.setInterval(syncPageContext, 1000)

  const context = () => ({
    page_url: window.location.href,
    page_title: document.title.slice(0, 512),
    locale: document.documentElement.lang || navigator.language,
    loader_version: '1.0.0',
  })

  const post = (path: string, body: object) =>
    fetch(`${apiBase}${path}`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(body),
      keepalive: true,
    })

  const hasLeadingZeroBits = (bytes: Uint8Array, bits: number) => {
    const wholeBytes = Math.floor(bits / 8)
    const remainder = bits % 8
    for (let index = 0; index < wholeBytes; index += 1) {
      if (bytes[index] !== 0) return false
    }
    if (!remainder) return true
    return (bytes[wholeBytes] & (0xff << (8 - remainder))) === 0
  }

  const solveChallenge = async (nonce: string, difficultyBits: number) => {
    if (difficultyBits === 0) return 0 // Deterministic unit-test challenge only.
    if (!crypto.subtle || difficultyBits < 0 || difficultyBits > 20) {
      throw new Error('challenge_unsupported')
    }
    const encoder = new TextEncoder()
    for (let proof = 0; proof <= 2_147_483_647; proof += 1) {
      const digest = new Uint8Array(
        await crypto.subtle.digest('SHA-256', encoder.encode(`${nonce}:${proof}`)),
      )
      if (hasLeadingZeroBits(digest, difficultyBits)) return proof
    }
    throw new Error('challenge_failed')
  }

  const requestBootstrap = async () => {
    const challengeResponse = await post(
      `/v1/widget/deployments/${encodeURIComponent(publicKey)}/challenge`,
      {
        page_url: window.location.href,
        loader_version: '1.0.0',
      },
    )
    if (!challengeResponse.ok) throw new Error('challenge_failed')
    const challenge = (await challengeResponse.json()) as {
      challenge_token: string
      nonce: string
      difficulty_bits: number
    }
    const challengeProof = await solveChallenge(
      challenge.nonce,
      challenge.difficulty_bits,
    )
    return post(
      `/v1/widget/deployments/${encodeURIComponent(publicKey)}/bootstrap`,
      {
        ...context(),
        challenge_token: challenge.challenge_token,
        challenge_proof: challengeProof,
      },
    )
  }

  void post(
    `/v1/widget/deployments/${encodeURIComponent(publicKey)}/heartbeat`,
    {
      page_url: window.location.href,
      loader_version: '1.0.0',
    },
  )
    .then(async (response) => {
      if (!response.ok) return
      const heartbeat = (await response.json()) as {
        available?: boolean
        launcher_label?: string
        position?: string
        primary_color?: string
        launcher_mode?: LauncherMode
      }
      if (heartbeat.launcher_label) {
        launcherLabel = heartbeat.launcher_label
        label.textContent = launcherLabel
      }
      applyPosition(heartbeat.position)
      applyPrimaryColor(heartbeat.primary_color)
      applyLauncherMode(heartbeat.launcher_mode)
      launcher.hidden = heartbeat.available !== true
    })
    .catch(() => undefined)

  const minimize = () => {
    if (frame) frame.hidden = true
    launcher.hidden = false
    launcher.focus({ preventScroll: true })
  }

  const close = () => {
    frame?.remove()
    frame = null
    bootstrapMessage = null
    launcher.hidden = false
    launcher.focus({ preventScroll: true })
  }

  const sendBootstrap = () => {
    if (frame?.contentWindow && bootstrapMessage) {
      frame.contentWindow.postMessage(bootstrapMessage, widgetOrigin)
    }
  }

  const refreshBootstrap = async () => {
    if (!frame || refreshing) return
    refreshing = true
    try {
      const response = await requestBootstrap()
      if (!response.ok) throw new Error('bootstrap_failed')
      const bootstrap = (await response.json()) as {
        config?: { launcher_label?: string; position?: string; primary_color?: string; text_enabled?: boolean; voice_enabled?: boolean }
      }
      launcherLabel = bootstrap.config?.launcher_label || launcherLabel
      applyPosition(bootstrap.config?.position)
      applyPrimaryColor(bootstrap.config?.primary_color)
      applyLauncherMode(modeFromConfig(bootstrap.config))
      bootstrapMessage = {
        type: 'rynvio:bootstrap-refresh',
        apiBase,
        hostOrigin,
        pageUrl: lastPageUrl,
        pageTitle: lastPageTitle,
        referrerUrl: contextReferrer,
        bootstrap,
      }
      sendBootstrap()
    } catch {
      frame.contentWindow?.postMessage(
        { type: 'rynvio:bootstrap-refresh-failed' },
        widgetOrigin,
      )
    } finally {
      refreshing = false
    }
  }

  const open = async () => {
    if (opening) return
    syncPageContext()
    if (frame) {
      frame.hidden = false
      launcher.hidden = true
      frame.focus()
      return
    }
    opening = true
    launcher.setAttribute('aria-busy', 'true')
    label.textContent = '接続しています…'
    try {
      const response = await requestBootstrap()
      if (!response.ok) throw new Error('bootstrap_failed')
      const bootstrap = (await response.json()) as { config?: { launcher_label?: string; position?: string; primary_color?: string; text_enabled?: boolean; voice_enabled?: boolean } }
      launcherLabel = bootstrap.config?.launcher_label || launcherLabel
      label.textContent = launcherLabel
      applyPosition(bootstrap.config?.position)
      applyPrimaryColor(bootstrap.config?.primary_color)
      applyLauncherMode(modeFromConfig(bootstrap.config))
      frame = document.createElement('iframe')
      frame.className = 'frame'
      frame.src = `${widgetOrigin}/`
      frame.title = 'Rynvio AI受付'
      frame.allow = 'microphone'
      frame.referrerPolicy = 'strict-origin-when-cross-origin'
      frame.setAttribute(
        'sandbox',
        'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
      )
      bootstrapMessage = {
        type: 'rynvio:bootstrap',
        apiBase,
        hostOrigin,
        pageUrl: lastPageUrl,
        pageTitle: lastPageTitle,
        referrerUrl: contextReferrer,
        bootstrap,
      }
      frame.addEventListener('load', sendBootstrap, { once: true })
      root.append(frame)
      launcher.hidden = true
      frame.focus()
    } catch {
      label.textContent = '現在利用できません'
      window.setTimeout(() => {
        label.textContent = launcherLabel
      }, 2500)
    } finally {
      opening = false
      launcher.removeAttribute('aria-busy')
    }
  }

  launcher.addEventListener('click', () => void open())
  window.addEventListener('message', (event) => {
    if (event.origin !== widgetOrigin || event.source !== frame?.contentWindow) return
    const data = event.data as { type?: string }
    if (data?.type === 'rynvio:ready') sendBootstrap()
    if (data?.type === 'rynvio:minimize') minimize()
    if (data?.type === 'rynvio:closed') close()
    if (data?.type === 'rynvio:refresh-bootstrap') void refreshBootstrap()
  })
})()
